import React, { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck,
  Clock,
  AlertCircle,
  Eye,
  MoreVertical,
  Plus,
} from "lucide-react";
import {
  getProviderCredentials,
  deleteCredential,
} from "../APIs/credentialApi";
import CredentialModal from "./CredentialModal";
import CredentialFilesModal from "./CredentialFilesModal";

const TYPE_LABEL = {
  LICENSE: "License",
  CERTIFICATE: "Certificate",
  CERTIFICATION: "Certification",
  INSURANCE: "Insurance",
  DOCUMENT: "Document",
  MALPRACTICE: "Malpractice",
  DEA: "DEA",
  NPI: "NPI",
};

const STATUS_CONFIG = {
  VERIFIED: { label: "Verified", color: "bg-green-100 text-green-700" },
  PENDING_REVIEW: {
    label: "Pending Review",
    color: "bg-gray-100 text-gray-500",
  },
  EXPIRING_SOON: {
    label: "Expiring Soon",
    color: "bg-orange-100 text-orange-600",
  },
  EXPIRED: { label: "Expired", color: "bg-red-100 text-red-600" },
};

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function SummaryCard({ icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className="shrink-0">{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900 leading-tight">
          {value}
        </p>
        <p className="text-xs text-gray-400">{sub}</p>
      </div>
    </div>
  );
}

export default function CredentialsTab({ providerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [activeSubTab, setActiveSubTab] = useState("all");
  const [modal, setModal] = useState(null); // null | { mode: 'add' | 'edit', credential? }
  const [filesModal, setFilesModal] = useState(null); // credential object
  const [menuOpen, setMenuOpen] = useState(null); // credentialId
  const [confirmDelete, setConfirmDelete] = useState(null); // credentialId
  const [deleting, setDeleting] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProviderCredentials(providerId);
      setData(res);
    } catch {
      setData({
        summary: {
          totalCount: 0,
          verifiedCount: 0,
          expiringSoonCount: 0,
          expiredCount: 0,
        },
        credentials: [],
      });
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (credentialId) => {
    setDeleting((d) => ({ ...d, [credentialId]: true }));
    try {
      await deleteCredential(credentialId, providerId);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting((d) => ({ ...d, [credentialId]: false }));
    }
  };

  const filtered = (data?.credentials ?? [])
    .filter((c) => {
      if (activeSubTab === "expiring")
        return c.displayStatus === "EXPIRING_SOON";
      if (activeSubTab === "expired") return c.displayStatus === "EXPIRED";
      return true;
    })
    .filter((c) => {
      if (
        search &&
        !c.credentialName.toLowerCase().includes(search.toLowerCase())
      )
        return false;
      if (typeFilter && c.credentialType !== typeFilter) return false;
      return true;
    });

  const s = data?.summary;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Credentials</h2>
          <p className="text-sm text-gray-500">
            Manage your professional credentials and documents.
          </p>
        </div>
        <button
          onClick={() => setModal({ mode: "add" })}
          className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700"
        >
          <Plus className="w-4 h-4" />
          Add Credential
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard
          icon={<ShieldCheck className="w-8 h-8 text-teal-600" />}
          label="Total Credentials"
          value={s?.totalCount ?? 0}
          sub="All credentials added"
        />
        <SummaryCard
          icon={<ShieldCheck className="w-8 h-8 text-green-500" />}
          label="Verified"
          value={s?.verifiedCount ?? 0}
          sub="Up to date"
        />
        <SummaryCard
          icon={<Clock className="w-8 h-8 text-orange-400" />}
          label="Expiring Soon"
          value={s?.expiringSoonCount ?? 0}
          sub="Within 90 days"
        />
        <SummaryCard
          icon={<AlertCircle className="w-8 h-8 text-red-400" />}
          label="Expired"
          value={s?.expiredCount ?? 0}
          sub="Action required"
        />
      </div>

      {/* Table panel */}
      <div className="bg-white rounded-xl border relative">
        {/* Sub-tabs + search + filter */}
        <div className="flex items-center justify-between px-4 py-3 border-b gap-3 flex-wrap">
          <div className="flex gap-4">
            {[
              { key: "all", label: "All Credentials" },
              { key: "expiring", label: "Expiring Soon" },
              { key: "expired", label: "Expired" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveSubTab(t.key)}
                className={`text-sm pb-1 font-medium border-b-2 transition-colors ${
                  activeSubTab === t.key
                    ? "border-teal-600 text-teal-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search credentials..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-48"
              />
              <svg
                className="absolute left-2.5 top-2 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Types</option>
              {Object.entries(TYPE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-[2fr_120px_110px_120px_130px_80px_80px] px-4 py-2.5 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <span>Credential</span>
          <span>Type</span>
          <span>Issue Date</span>
          <span>Expiration Date</span>
          <span>Status</span>
          <span>Documents</span>
          <span>Actions</span>
        </div>

        {/* Rows */}
        {loading && (
          <p className="text-sm text-gray-400 text-center py-8">
            Loading credentials...
          </p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">
            No credentials found.
          </p>
        )}
        {!loading &&
          filtered.map((c, i) => {
            const statusCfg =
              STATUS_CONFIG[c.displayStatus] ?? STATUS_CONFIG.PENDING_REVIEW;
            const isConfirming = confirmDelete === c.credentialId;

            return (
              <div
                key={c.credentialId}
                className={`grid grid-cols-[2fr_120px_110px_120px_130px_80px_80px] items-center px-4 py-3 text-sm ${
                  i < filtered.length - 1 ? "border-b border-gray-100" : ""
                }`}
              >
                {/* Credential name */}
                <span className="font-medium text-gray-900 truncate pr-2">
                  {c.credentialName}
                </span>

                {/* Type */}
                <span className="text-gray-500 text-xs">
                  {TYPE_LABEL[c.credentialType] ?? c.credentialType ?? "—"}
                </span>

                {/* Issue Date */}
                <span className="text-gray-500 text-xs">
                  {fmt(c.issueDate)}
                </span>

                {/* Expiration Date */}
                <span
                  className={`text-xs font-medium ${
                    c.displayStatus === "EXPIRING_SOON"
                      ? "text-orange-600"
                      : c.displayStatus === "EXPIRED"
                        ? "text-red-600"
                        : "text-gray-500"
                  }`}
                >
                  {c.doesNotExpire ? "—" : fmt(c.expirationDate)}
                </span>

                {/* Status */}
                <span
                  className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium w-fit ${statusCfg.color}`}
                >
                  {statusCfg.label}
                </span>

                {/* Documents count */}
                <span className="text-gray-500 text-xs">
                  {c.documentCount} file{c.documentCount !== 1 ? "s" : ""}
                </span>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {isConfirming ? (
                    <div className="flex items-center gap-1">
                      <button
                        disabled={deleting[c.credentialId]}
                        onClick={() => handleDelete(c.credentialId)}
                        className="text-xs px-2 py-1 rounded bg-red-600 text-white disabled:opacity-40"
                      >
                        {deleting[c.credentialId] ? "..." : "Confirm"}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="text-xs px-2 py-1 rounded border"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => setFilesModal(c)}
                        className="text-gray-400 hover:text-teal-600 transition"
                        title="View documents"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <div className="relative">
                        <button
                          onClick={() =>
                            setMenuOpen(
                              menuOpen === c.credentialId
                                ? null
                                : c.credentialId,
                            )
                          }
                          className="text-gray-400 hover:text-gray-600 transition"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {menuOpen === c.credentialId && (
                          <div className="absolute right-0 top-6 z-50 bg-white border rounded-lg shadow-lg py-1 w-32">
                            <button
                              onClick={() => {
                                setMenuOpen(null);
                                setModal({ mode: "edit", credential: c });
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                setMenuOpen(null);
                                setConfirmDelete(c.credentialId);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* Modals */}
      {modal && (
        <CredentialModal
          providerId={providerId}
          credential={modal.credential ?? null}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      )}
      {filesModal && (
        <CredentialFilesModal
          credential={filesModal}
          onClose={() => setFilesModal(null)}
        />
      )}

      {/* Click-outside to close menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
      )}
    </div>
  );
}
